"use client";

import flags from "react-phone-number-input/flags";
import type { Country } from "react-phone-number-input";
import { ChevronDownIcon } from "lucide-react";
import { useHydrated } from "@/hooks/use-hydrated";
import type { Market } from "@/lib/api/checkout.schema";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function MarketFlag({ country }: { country: string }) {
  const Flag = flags[country as Country];
  return Flag ? (
    <span className="flex w-5 overflow-hidden rounded-sm">
      <Flag title={country} />
    </span>
  ) : null;
}

const triggerClassNames = {
  // Plain text beside a label
  inline:
    "h-7 w-auto gap-1 border-0 bg-transparent px-1.5 text-sm text-muted-foreground shadow-none",
  // A full-width form field, like the other inputs
  field: "h-11 w-full gap-2 px-3",
};

/**
 * Market picker beside an amount: lets the payer preview prices in another
 * market's currency. Purely a preview - their phone number decides what they
 * are charged. Radix Select generates ids that differ between server and
 * client, so a static lookalike is rendered until hydration completes (same
 * approach as PhoneInput's country picker).
 */
export function MarketSelect({
  markets,
  value,
  onChange,
  label,
  variant = "inline",
  id,
}: {
  markets: Market[];
  value: Market;
  onChange: (country: string) => void;
  label: string;
  variant?: keyof typeof triggerClassNames;
  id?: string;
}) {
  const hydrated = useHydrated();
  const triggerClassName = triggerClassNames[variant];

  if (!hydrated) {
    return (
      <span
        className={`inline-flex items-center rounded-md ${
          variant === "field" ? "border border-input text-sm" : ""
        } ${triggerClassName}`}
        aria-hidden
      >
        <MarketFlag country={value.country} />
        {value.currency}
        <ChevronDownIcon
          className={`size-4 opacity-50 ${variant === "field" ? "ml-auto" : ""}`}
        />
      </span>
    );
  }

  return (
    <Select value={value.country} onValueChange={onChange}>
      <SelectTrigger
        id={id}
        size={variant === "field" ? "default" : "sm"}
        className={triggerClassName}
        aria-label={label}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="start">
        {markets.map((market) => (
          <SelectItem key={market.country} value={market.country}>
            <MarketFlag country={market.country} />
            {market.currency}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
